import React, { useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faCamera } from '@fortawesome/free-solid-svg-icons';
import { guestApi } from '../../utils/portalApi';
import Avatar from '../Avatar';
import GuestPassword from './GuestPassword';

/**
 * The guest's own basic profile (guest role, Phase 1).
 *
 * Name, phone, company and picture are theirs to change. Email is not: it is
 * how every invitation finds them, so changing it is a job for the company.
 */
const GuestProfile = ({ me, onSaved }) => {
    const [form, setForm] = useState({ name: me.name, phone: me.phone, company: me.company });
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [notice, setNotice] = useState({ tone: '', text: '' });
    const fileRef = useRef(null);

    const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

    const save = async (e) => {
        e.preventDefault();
        setBusy(true);
        setNotice({ tone: '', text: '' });
        try {
            const { data } = await guestApi.put('/me', form);
            onSaved({ ...me, ...data });
            setNotice({ tone: 'ok', text: 'Saved.' });
        } catch (err) {
            setNotice({ tone: 'error', text: err.response?.data?.message || 'Could not save your profile.' });
        } finally {
            setBusy(false);
        }
    };

    const upload = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setUploading(true);
        setNotice({ tone: '', text: '' });
        try {
            const body = new FormData();
            body.append('photo', file);
            const { data } = await guestApi.post('/me/photo', body, { headers: { 'Content-Type': 'multipart/form-data' } });
            onSaved({ ...me, ...data });
        } catch (err) {
            setNotice({ tone: 'error', text: err.response?.data?.message || 'Could not upload your picture.' });
        } finally {
            setUploading(false);
        }
    };

    return (
        <main className="guest-page">
            <h1><FontAwesomeIcon icon={faUser} /> Profile</h1>
            <form className="guest-profile" onSubmit={save}>
                <div className="guest-photo">
                    <Avatar name={me.name} profilePic={me.profilePic} className="msgr-avatar" />
                    <button type="button" className="msgr-btn" disabled={uploading} onClick={() => fileRef.current?.click()}>
                        <FontAwesomeIcon icon={faCamera} /> {uploading ? 'Uploading…' : 'Change picture'}
                    </button>
                    <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={upload} />
                </div>

                <label className="portal-label">Name
                    <input className="ext-input" value={form.name} maxLength={120} required onChange={set('name')} />
                </label>
                <label className="portal-label">Email
                    <input className="ext-input" value={me.email} readOnly disabled />
                </label>
                <label className="portal-label">Phone
                    <input className="ext-input" value={form.phone} maxLength={30} onChange={set('phone')} />
                </label>
                <label className="portal-label">Company
                    <input className="ext-input" value={form.company} maxLength={120} onChange={set('company')} />
                </label>
                <p className="guest-muted">Guest ID {me.externalId}. Your email can only be changed by the team at GTS.</p>

                {notice.text && <p className={notice.tone === 'error' ? 'portal-error' : 'guest-ok'}>{notice.text}</p>}
                <button className="portal-primary" disabled={busy || !form.name.trim()}>{busy ? 'Saving…' : 'Save profile'}</button>
            </form>
            {!me.viewedByStaff && <GuestPassword me={me} onSaved={onSaved} />}
        </main>
    );
};

export default GuestProfile;
